/**
 * API Service for Alpha Discovery Dashboard
 * Handles all HTTP requests to the Cloudflare Functions API
 */

import { 
  ApiResponse, 
  StrategyEntriesResponse, 
  RunsResponse, 
  StatsResponse,
  StrategyEntry,
  RunMetadata 
} from '../types'

// API Configuration
const API_BASE_URL = '/api'
const DEFAULT_TIMEOUT = 30000 // 30 seconds

// Request configuration interface
interface RequestConfig {
  timeout?: number
  signal?: AbortSignal
  headers?: Record<string, string>
}

// Custom error class for API errors
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public response?: any
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// Helper function to create AbortSignal with timeout
function createTimeoutSignal(timeout: number): AbortSignal {
  const controller = new AbortController()
  setTimeout(() => controller.abort(), timeout)
  return controller.signal
}

// Generic fetch wrapper with error handling and timeout
async function apiRequest<T>(
  endpoint: string, 
  options: RequestInit & RequestConfig = {}
): Promise<ApiResponse<T>> {
  const { timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options
  
  try {
    // Create timeout signal if not provided
    const signal = options.signal || createTimeoutSignal(timeout)
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...fetchOptions,
      signal,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
        ...fetchOptions.headers
      }
    })

    if (!response.ok) {
      const errorText = await response.text()
      let errorData: any
      try {
        errorData = JSON.parse(errorText)
      } catch {
        errorData = { error: errorText }
      }
      
      throw new ApiError(
        errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        errorData
      )
    }

    const data: ApiResponse<T> = await response.json()
    
    if (!data.success) {
      throw new ApiError(data.error || 'API request failed', response.status, data)
    }

    return data

  } catch (error) {
    if (error instanceof ApiError) {
      throw error
    }
    
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError('Request timeout', 408)
    }
    
    throw new ApiError(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

// Strategy API functions
export const strategyApi = {
  /**
   * Fetch strategy entries with optional filters
   */
  async getStrategies(params: {
    run_id?: string
    time_range?: 'all' | '24h' | '7d' | '30d'
    survivors_only?: boolean
    page?: number
    page_size?: number
    sort_by?: string
    sort_order?: 'asc' | 'desc'
  } = {}): Promise<StrategyEntriesResponse> {
    const searchParams = new URLSearchParams()
    
    // Add parameters to search params
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value))
      }
    })
    
    const endpoint = `/strategies${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
    const response = await apiRequest<StrategyEntriesResponse>(endpoint)
    
    return response.data!
  },

  /**
   * Create or update strategy entries (batch operation)
   */
  async createStrategies(entries: Partial<StrategyEntry>[]): Promise<{ inserted: number; results: any[] }> {
    const response = await apiRequest<{ inserted: number; results: any[] }>('/strategies', {
      method: 'POST',
      body: JSON.stringify({ entries })
    })
    
    return response.data!
  },

  /**
   * Get strategies for a specific run
   */
  async getStrategiesByRun(runId: string, survivorsOnly = false): Promise<StrategyEntry[]> {
    const result = await this.getStrategies({ 
      run_id: runId, 
      survivors_only: survivorsOnly,
      page_size: 1000 // Get all strategies for the run
    })
    
    return result.entries
  },

  /**
   * Get recent strategies within time range
   */
  async getRecentStrategies(timeRange: '24h' | '7d' | '30d' = '24h'): Promise<StrategyEntry[]> {
    const result = await this.getStrategies({ 
      time_range: timeRange,
      page_size: 1000
    })
    
    return result.entries
  }
}

// Run metadata API functions
export const runApi = {
  /**
   * Fetch all runs or filter by active/completed status
   */
  async getRuns(params: {
    active_only?: boolean
    completed_only?: boolean
  } = {}): Promise<RunsResponse> {
    const searchParams = new URLSearchParams()
    
    Object.entries(params).forEach(([key, value]) => {
      if (value === true) {
        searchParams.append(key, 'true')
      }
    })
    
    const endpoint = `/runs${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
    const response = await apiRequest<RunsResponse>(endpoint)
    
    return response.data!
  },

  /**
   * Create or update run metadata (batch operation)
   */
  async createRuns(runs: Partial<RunMetadata>[]): Promise<{ inserted: number; results: any[] }> {
    const response = await apiRequest<{ inserted: number; results: any[] }>('/runs', {
      method: 'POST',
      body: JSON.stringify({ runs })
    })
    
    return response.data!
  },

  /**
   * Get only active runs
   */
  async getActiveRuns(): Promise<RunMetadata[]> {
    const result = await this.getRuns({ active_only: true })
    return result.runs
  },

  /**
   * Get only completed runs
   */
  async getCompletedRuns(): Promise<RunMetadata[]> {
    const result = await this.getRuns({ completed_only: true })
    return result.runs
  }
}

// Stats API functions  
export const statsApi = {
  /**
   * Get dashboard statistics
   */
  async getStats(): Promise<StatsResponse> {
    const response = await apiRequest<StatsResponse>('/stats')
    return response.data!
  }
}

// Combined API client
export const apiClient = {
  strategies: strategyApi,
  runs: runApi,
  stats: statsApi,
  
  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    try {
      const response = await apiRequest<{ status: string }>('/health', {
        timeout: 5000
      })
      return { 
        status: 'healthy', 
        timestamp: response.timestamp 
      }
    } catch (error) {
      return { 
        status: 'unhealthy', 
        timestamp: new Date().toISOString() 
      }
    }
  },

  /**
   * Load all essential data for dashboard initialization
   */
  async loadDashboardData(): Promise<{
    strategies: StrategyEntry[]
    runs: RunMetadata[]
    stats: StatsResponse
  }> {
    try {
      // Execute all requests in parallel for better performance
      const [strategiesResult, runsResult, statsResult] = await Promise.all([
        strategyApi.getStrategies({ page_size: 1000 }), // Get all strategies
        runApi.getRuns(),
        statsApi.getStats()
      ])

      return {
        strategies: strategiesResult.entries,
        runs: runsResult.runs,
        stats: statsResult
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
      throw error
    }
  }
}

// Export individual APIs and main client
export default apiClient